/**
 * SHX Steps — bottom navigation for the step carousel: arrows (mobile and
 * desktop-carousel mode) and dots (mobile), synced to the native scroll-snap
 * row. Same proven scroller pattern as the SHX reviews carousel.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ShxStepsSlider extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    const row = this.querySelector('[data-shx-steps-row]');
    if (!row) return;
    const prev = this.querySelector('[data-shx-steps-prev]');
    const next = this.querySelector('[data-shx-steps-next]');
    const dots = Array.from(this.querySelectorAll('[data-shx-steps-dot]'));
    const dotsWrap = this.querySelector('.shx-steps__dots');
    const dirFlip = document.body.dataset.rtl === 'true' ? -1 : 1;

    // One "step" of travel = one card + the row gap, measured live so every
    // width/columns/peek setting is respected.
    const cardStep = () => {
      const card = row.querySelector('.shx-steps__card');
      if (!card) return row.clientWidth;
      const styles = getComputedStyle(row);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const index = () => {
      const step = cardStep();
      return step > 0 ? Math.round(Math.abs(row.scrollLeft) / step) : 0;
    };

    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      const pos = Math.abs(row.scrollLeft);
      const scrollable = max > 4;
      // Both arrows hide entirely when nothing can scroll; while scrollable,
      // the unavailable direction dims (disabled) instead of disappearing,
      // so the layout never jumps and the state reads clearly.
      if (prev) {
        prev.hidden = !scrollable;
        prev.disabled = pos <= 4;
      }
      if (next) {
        next.hidden = !scrollable;
        next.disabled = pos >= max - 4;
      }
      // Dots hide when nothing scrolls, and trailing dots past the last
      // reachable snap position trim away (with 2 cards per view the final
      // view holds the last TWO cards — there is one fewer position).
      const positions = scrollable ? Math.round(max / cardStep()) + 1 : 1;
      if (dotsWrap) dotsWrap.hidden = !scrollable;
      const active = index();
      // SHX §3.20: the Liquid label says "go to N of <total blocks>", but
      // trailing dots are trimmed here at runtime — so a screen reader was
      // told "3 of 5" while only 3 dots existed. Re-label against the number
      // of dots actually reachable.
      const reachable = Math.min(positions, dots.length);
      dots.forEach((dot, i) => {
        dot.hidden = i >= positions;
        dot.classList.toggle('is-active', i === active);
        if (i === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        if (!dot.hidden) {
          const label = dot.getAttribute('aria-label');
          if (label) dot.setAttribute('aria-label', label.replace(/\d+\s*$/, String(reachable)));
        }
      });
    };

    const scrollToIndex = (i) => {
      row.scrollTo({
        left: i * cardStep() * dirFlip,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    };

    if (prev) prev.addEventListener('click', () => scrollToIndex(Math.max(0, index() - 1)), { signal });
    if (next) next.addEventListener('click', () => scrollToIndex(index() + 1), { signal });
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => scrollToIndex(i), { signal });
    });

    let raf = 0;
    row.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      },
      { signal, passive: true }
    );

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(update);
      this.ro.observe(row);
    } else {
      window.addEventListener('resize', update, { signal });
    }
    update();
  }

  disconnectedCallback() {
    if (this.abort) this.abort.abort();
    if (this.ro) this.ro.disconnect();
  }
}

if (!customElements.get('shx-steps-slider')) {
  customElements.define('shx-steps-slider', ShxStepsSlider);
}
